import React, { useState } from 'react';
import { Card, List, Tag, Collapse, Empty, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useControlTowerAlerts } from '@/hooks/useManufacturingControlTower';

import type { ControlTowerQueryParams } from '@/services/manufacturingControlTower.service';

interface Props {
  filters?: ControlTowerQueryParams;
  refreshInterval?: number;
}

interface TowerAlert {
  id: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  deepLink?: string;
  workOrderId?: string;
  actionRequired?: string;
}

const SmartAlertsPanel: React.FC<Props> = ({ filters, refreshInterval }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useControlTowerAlerts(filters, refreshInterval);
  const alerts = data as {
    critical?: TowerAlert[];
    warnings?: TowerAlert[];
    info?: TowerAlert[];
  };
  const [expanded, setExpanded] = useState<string[]>(['critical']);

  const go = (a: TowerAlert) => {
    if (a.deepLink) navigate(a.deepLink);
    else if (a.workOrderId) navigate(`/mfg/work-orders/${a.workOrderId}`);
  };

  const renderList = (list: TowerAlert[] | undefined, color: string) => {
    if (!list?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="None" />;
    return (
      <List
        size="small"
        dataSource={list}
        renderItem={(a) => (
          <List.Item style={{ cursor: 'pointer', borderLeft: `4px solid ${color}` }} onClick={() => go(a)}>
            <List.Item.Meta
              title={
                <>
                  <Tag>{a.type}</Tag> {a.title}
                </>
              }
              description={
                <>
                  {a.description}
                  {a.actionRequired ? (
                    <div>
                      <Tag color="processing">Do now</Tag> {a.actionRequired}
                    </div>
                  ) : null}
                </>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  if (isLoading) return <Spin />;

  return (
    <Card id="alerts" title="Smart alerts" style={{ marginTop: 16 }}>
      <Collapse
        activeKey={expanded}
        onChange={(k) => setExpanded(k as string[])}
        items={[
          { key: 'critical', label: `Critical (${alerts?.critical?.length ?? 0})`, children: renderList(alerts?.critical, '#ff4d4f') },
          { key: 'warnings', label: `Warnings (${alerts?.warnings?.length ?? 0})`, children: renderList(alerts?.warnings, '#faad14') },
          { key: 'info', label: `Info (${alerts?.info?.length ?? 0})`, children: renderList(alerts?.info, '#145A54') },
        ]}
      />
    </Card>
  );
};

export default SmartAlertsPanel;
